import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ProductoCementoVm } from '../../../../models/producto-cemento.vm';
import { ProductoCementoUpsertCommand } from '../../../../models/producto-cemento.model';

const COMUNAS_OPCIONES = [
  'Iquique',
  'Alto Hospicio',
  'Antofagasta',
  'Las Condes',
  'Providencia',
  'Maipú',
  'Puente Alto',
  'Santiago Centro',
  'Concepción',
  'Valparaíso',
  'Temuco',
  'Osorno',
  'Puerto Montt',
].sort((a, b) => a.localeCompare(b));

const ACTIVO_OPCIONES = [
  { label: 'Sí', value: true },
  { label: 'No', value: false },
];

const UNIDAD_OPCIONES = ['Sacos'].sort((a, b) =>
  a.localeCompare(b),
);

const MULTIPLO_PASO = 72;

function multiploValidator(control: AbstractControl): ValidationErrors | null {
  const raw = control.value;
  if (raw === '' || raw === null || raw === undefined) {
    return { required: true };
  }
  const n = typeof raw === 'number' ? raw : Number(String(raw).trim());
  if (!Number.isFinite(n) || !Number.isInteger(n)) {
    return { multiploInvalid: true };
  }
  if (n < MULTIPLO_PASO || n % MULTIPLO_PASO !== 0) {
    return { multiploInvalid: true };
  }
  return null;
}

function requiredNonNegativeNumberValidator(control: AbstractControl): ValidationErrors | null {
  const raw = control.value;
  if (raw === '' || raw === null || raw === undefined) {
    return { required: true };
  }
  const n = typeof raw === 'number' ? raw : Number(String(raw).trim());
  if (!Number.isFinite(n) || n < 0) {
    return { precioBrutoInvalid: true };
  }
  return null;
}

function nonEmptyTrimmedValidator(control: AbstractControl): ValidationErrors | null {
  const raw = control.value;
  if (typeof raw !== 'string' || raw.trim() === '') {
    return { required: true };
  }
  return null;
}

type ProductoCementoForm = FormGroup<{
  nombreComuna: FormControl<string>;
  producto: FormControl<string>;
  nombrePublicacion: FormControl<string>;
  multiplo: FormControl<number | string>;
  unidad: FormControl<string>;
  incoterms: FormControl<string>;
  skuPos10: FormControl<string>;
  skuPos20: FormControl<string>;
  skuPos30: FormControl<string>;
  precioBruto: FormControl<number | string>;
  tipoCamion: FormControl<string>;
  activo: FormControl<boolean>;
}>;

type ControlKey = keyof ProductoCementoForm['controls'];

@Component({
  selector: 'app-producto-cemento-detalle-modal',
  templateUrl: './producto-cemento-detalle-modal.component.html',
  styleUrls: ['./producto-cemento-detalle-modal.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductoCementoDetalleModalComponent implements OnChanges {
  @Input() item: ProductoCementoVm | null = null;
  @Input() loading = false;

  @Output() closeRequested = new EventEmitter<void>();
  @Output() saveRequested = new EventEmitter<ProductoCementoUpsertCommand>();
  @Output() deleteRequested = new EventEmitter<void>();

  readonly form: ProductoCementoForm;
  readonly comunasOpciones = COMUNAS_OPCIONES;
  readonly activoOpciones = ACTIVO_OPCIONES;
  unidadOpciones: string[] = [...UNIDAD_OPCIONES];

  constructor(private readonly fb: FormBuilder) {
    this.form = this.fb.group({
      nombreComuna: this.fb.nonNullable.control('', { validators: [Validators.required] }),
      producto: this.fb.nonNullable.control('', { validators: [Validators.required] }),
      nombrePublicacion: this.fb.nonNullable.control(''),
      multiplo: this.fb.control<number | string>(MULTIPLO_PASO, { validators: [multiploValidator] }),
      unidad: this.fb.nonNullable.control('Sacos', { validators: [Validators.required] }),
      incoterms: this.fb.nonNullable.control('', { validators: [Validators.required] }),
      skuPos10: this.fb.nonNullable.control('', { validators: [nonEmptyTrimmedValidator] }),
      skuPos20: this.fb.nonNullable.control(''),
      skuPos30: this.fb.nonNullable.control(''),
      precioBruto: this.fb.control<number | string>('', { validators: [requiredNonNegativeNumberValidator] }),
      tipoCamion: this.fb.nonNullable.control('', { validators: [Validators.required] }),
      activo: this.fb.nonNullable.control(true),
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['item']) {
      this.refreshUnidadOpciones();
      this.applyItemToForm();
    }
  }

  get isEditMode(): boolean {
    return (this.item?.id ?? 0) > 0;
  }

  get modalTitle(): string {
    return this.isEditMode ? 'Editar producto cemento' : 'Crear producto cemento';
  }

  fieldError(controlName: ControlKey): string {
    const control = this.form.controls[controlName];
    if (!control.touched || !control.invalid) {
      return '';
    }
    const errors = control.errors;
    if (errors?.['required']) {
      return 'Campo requerido';
    }
    if (errors?.['multiploInvalid']) {
      return 'Debe ser un múltiplo de 72 (72, 144, 216, …)';
    }
    if (errors?.['precioBrutoInvalid']) {
      return 'Ingrese un número mayor o igual a 0';
    }
    return 'Revise el valor';
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const multiploRaw = this.form.controls.multiplo.getRawValue();
    const multiplo =
      typeof multiploRaw === 'number' ? multiploRaw : Number(String(multiploRaw).trim());

    const precioRaw = this.form.controls.precioBruto.getRawValue();
    const precioBruto =
      typeof precioRaw === 'number' ? precioRaw : Number(String(precioRaw).trim());

    const unidadRaw = this.form.controls.unidad.getRawValue();
    const unidad =
      typeof unidadRaw === 'string' ? unidadRaw.trim() : String(unidadRaw ?? '').trim();

    this.saveRequested.emit({
      nombreComuna: this.form.controls.nombreComuna.getRawValue().trim(),
      producto: this.form.controls.producto.getRawValue().trim(),
      nombrePublicacion: this.form.controls.nombrePublicacion.getRawValue().trim(),
      multiplo,
      unidad,
      incoterms: this.form.controls.incoterms.getRawValue().trim(),
      skuPos10: this.form.controls.skuPos10.getRawValue().trim(),
      skuPos20: this.form.controls.skuPos20.getRawValue().trim(),
      skuPos30: this.form.controls.skuPos30.getRawValue().trim(),
      precioBruto,
      tipoCamion: this.form.controls.tipoCamion.getRawValue().trim(),
      activo: this.form.controls.activo.getRawValue(),
    });
  }

  onClose(): void {
    this.closeRequested.emit();
  }

  onDelete(): void {
    this.deleteRequested.emit();
  }

  private refreshUnidadOpciones(): void {
    const opciones = [...UNIDAD_OPCIONES];
    const u = this.item?.unidad?.trim();
    if (u && !opciones.includes(u)) {
      opciones.push(u);
      opciones.sort((a, b) => a.localeCompare(b));
    }
    this.unidadOpciones = opciones;
  }

  private applyItemToForm(): void {
    if (!this.item) {
      this.form.reset({
        nombreComuna: '',
        producto: '',
        nombrePublicacion: '',
        multiplo: MULTIPLO_PASO,
        unidad: 'Sacos',
        incoterms: '',
        skuPos10: '',
        skuPos20: '',
        skuPos30: '',
        precioBruto: '',
        tipoCamion: '',
        activo: true,
      });
      return;
    }

    this.form.reset({
      nombreComuna: this.item.nombreComuna,
      producto: this.item.producto,
      nombrePublicacion: this.item.nombrePublicacion,
      multiplo: this.item.multiplo > 0 ? this.item.multiplo : MULTIPLO_PASO,
      unidad: this.item.unidad,
      incoterms: this.item.incoterms,
      skuPos10: this.item.skuPos10,
      skuPos20: this.item.skuPos20,
      skuPos30: this.item.skuPos30,
      precioBruto: this.item.precioBruto ?? '',
      tipoCamion: this.item.tipoCamion,
      activo: this.item.activo,
    });
  }
}
